import React, { useState, useEffect } from 'react';
import { 
    ShieldExclamationIcon, 
    BriefcaseIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    ArrowPathIcon,
    ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline';
import { db } from '../../firebaseConfig';
import { 
    collection, 
    query, 
    where, 
    onSnapshot, 
    orderBy,
    doc,
    updateDoc,
    setDoc,
    deleteDoc
} from 'firebase/firestore';
import type { Organization, SrmProject, RiskEntry } from '@types';
import SrmRiskRegister from './SrmRiskRegister';

interface GlobalMasterRiskRegisterProps {
    organization: Organization;
    onNavigateToProject: (projectId: string) => void;
    readOnly?: boolean;
}

const GlobalMasterRiskRegister: React.FC<GlobalMasterRiskRegisterProps> = ({ 
    organization,
    onNavigateToProject,
    readOnly = false
}) => {
    const [globalRisks, setGlobalRisks] = useState<RiskEntry[]>([]);
    const [activeProjects, setActiveProjects] = useState<SrmProject[]>([]);
    const [expandedProjectId, setExpandedProjectId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (!organization?.id || !db) return;

        // 1. Listen for Global Risks
        const globalRisksQuery = query(
            collection(db, 'organizations', organization.id, 'global_risk_register'),
            orderBy('createdAt', 'desc')
        );

        const unsubGlobal = onSnapshot(globalRisksQuery, (snap) => {
            const risks = snap.docs.map(doc => ({ ...doc.data(), id: doc.id } as RiskEntry));
            setGlobalRisks(risks);
            setIsLoading(false);
        }, (err) => {
            console.error("Global risks listener err:", err);
            setIsLoading(false);
        });

        // 2. Listen for Active Projects
        const projectsQuery = query(
            collection(db, 'srm_projects'),
            where('organizationId', '==', organization.id)
        );

        const unsubProjects = onSnapshot(projectsQuery, (snap) => {
            const projects = snap.docs.map(doc => ({ ...doc.data(), id: doc.id } as SrmProject))
                .filter(p => p.status === 'Active');
            setActiveProjects(projects);
        }, (err) => console.error("Projects listener err:", err));

        return () => {
            unsubGlobal();
            unsubProjects();
        };
    }, [organization?.id]);

    const handleUpdateGlobalRisks = async (updatedRisks: RiskEntry[]) => {
        if (!organization?.id || !db) return;

        // Find what changed
        const existingIds = new Set(globalRisks.map(r => r.id));
        const updatedIds = new Set(updatedRisks.map(r => r.id));

        // 1. Handle Deletions
        const deletedIds = globalRisks.filter(r => !updatedIds.has(r.id)).map(r => r.id);
        for (const id of deletedIds) {
            try {
                await deleteDoc(doc(db, 'organizations', organization.id, 'global_risk_register', id));
                console.log(`Global Risk ${id} deleted.`);
            } catch (err) {
                console.error(`Failed to delete global risk ${id}:`, err);
            }
        }

        // 2. Handle Additions & Updates
        for (const risk of updatedRisks) {
            const riskId = risk.id;
            const existingRisk = globalRisks.find(r => r.id === riskId);
            
            // Only update if it's new or content changed
            if (!existingRisk || JSON.stringify(existingRisk) !== JSON.stringify(risk)) {
                try {
                    const riskDocRef = doc(db, 'organizations', organization.id, 'global_risk_register', riskId);
                    await setDoc(riskDocRef, {
                        ...risk,
                        organizationId: organization.id,
                        updatedAt: new Date().toISOString(),
                        createdAt: risk.createdAt || new Date().toISOString()
                    }, { merge: true });
                    console.log(`Global Risk ${riskId} synchronized.`);
                } catch (err) {
                    console.error(`Failed to sync global risk ${riskId}:`, err);
                }
            }
        }
        
        // Local state is updated via the onSnapshot listener automatically, 
        // but setGlobalRisks(updatedRisks) provides immediate UI feedback.
        setGlobalRisks(updatedRisks);
    };

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center p-12 space-y-4">
                <ArrowPathIcon className="w-8 h-8 text-indigo-600 animate-spin" />
                <p className="text-slate-500 font-bold uppercase tracking-widest text-xs">Synchronizing Master Risk Registry...</p>
            </div>
        );
    }

    return (
        <div className="space-y-12 animate-fade-in pb-20">
            {/* 1. Global (Organization-wide) Risks */}
            <section>
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center text-white shadow-lg shadow-indigo-200">
                            <ShieldExclamationIcon className="w-6 h-6" />
                        </div>
                        <div>
                            <h2 className="text-2xl font-black text-slate-900 uppercase tracking-tight">Global Master Register</h2>
                            <p className="text-sm text-slate-500 font-medium">Enterprise-wide threat matrix and cross-project risk aggregation.</p>
                        </div>
                    </div>
                </div>

                <SrmRiskRegister 
                    risks={globalRisks}
                    isGlobal={true}
                    title="Organization-Wide Risks"
                    description="Risks identified via ECC Insights auto-remediation and organizational compliance audits."
                    onUpdate={handleUpdateGlobalRisks}
                    readOnly={readOnly}
                />
            </section>

            {/* 2. Active Project Risks */}
            <section className="space-y-6">
                <div className="flex items-center gap-3 mb-2">
                    <div className="h-6 w-1.5 bg-blue-600 rounded-full"></div>
                    <h2 className="text-xl font-black text-slate-900 uppercase tracking-tight">Active SRM Projects</h2>
                </div>

                <div className="grid grid-cols-1 gap-6">
                    {activeProjects.length === 0 ? (
                        <div className="bg-slate-50 border border-slate-200 border-dashed rounded-3xl p-12 text-center">
                            <BriefcaseIcon className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                            <h4 className="text-lg font-bold text-slate-900 mb-1">No Active Frameworks</h4>
                            <p className="text-slate-500 max-w-md mx-auto">There are no active SRM projects currently monitoring risks. Initialize a project to see its tactical risks here.</p>
                        </div>
                    ) : (
                        activeProjects.map(project => (
                            <div key={project.id} className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-sm hover:shadow-md transition-all border-l-4 border-l-blue-600">
                                <div 
                                    className="p-6 flex flex-col md:flex-row md:items-center justify-between gap-6 cursor-pointer hover:bg-slate-50/50 transition-colors"
                                    onClick={() => setExpandedProjectId(expandedProjectId === project.id ? null : project.id)}
                                >
                                    <div className="flex items-center gap-5">
                                        <div className="w-14 h-14 rounded-2xl bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-600 relative">
                                            <BriefcaseIcon className="w-7 h-7" />
                                            {(project.risks || []).length > 0 && (
                                                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-black rounded-full flex items-center justify-center border-2 border-white">
                                                    {(project.risks || []).length}
                                                </span>
                                            )}
                                        </div>
                                        <div>
                                            <h4 className="text-lg font-black text-slate-900">{project.name || project.projectName}</h4>
                                            <div className="flex items-center gap-3 mt-1.5">
                                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{project.projectNumber || 'SRM-PROJ'}</span>
                                                <span className="w-1 h-1 rounded-full bg-slate-300"></span>
                                                <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{project.locationName || 'Multi-Site'}</span>
                                                <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded ${
                                                    (project.overallRiskScore || 0) >= 15 ? 'bg-red-100 text-red-700' : 
                                                    (project.overallRiskScore || 0) >= 10 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'
                                                }`}>
                                                    Score: {project.overallRiskScore || 0}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3 self-end md:self-center">
                                        {!readOnly && <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onNavigateToProject(project.id);
                                            }}
                                            className="px-4 py-2 bg-slate-900 text-white text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-slate-800 transition-all flex items-center gap-2 shadow-lg shadow-slate-200"
                                        >
                                            Open Dashboard
                                            <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                                        </button>}
                                        <div className="w-10 h-10 flex items-center justify-center text-slate-400">
                                            {expandedProjectId === project.id ? (
                                                <ChevronUpIcon className="w-6 h-6" />
                                            ) : (
                                                <ChevronDownIcon className="w-6 h-6" />
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {expandedProjectId === project.id && (
                                    <div className="border-t border-slate-100 p-8 bg-slate-50/50 animate-slide-down">
                                        <div className="mb-6 flex items-center justify-between">
                                            <h5 className="text-xs font-black text-slate-400 uppercase tracking-widest">Tactical Project Risks</h5>
                                            <span className="text-[10px] font-bold text-slate-500 italic">Showing all active threats for this project context</span>
                                        </div>
                                        
                                        {(project.risks || []).length === 0 ? (
                                            <div className="bg-white border border-slate-200 rounded-2xl p-6 text-center italic text-slate-400 text-sm">
                                                No active risks registered for this project.
                                            </div>
                                        ) : (
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                                {(project.risks || []).map((risk, idx) => (
                                                    <div key={risk.id || idx} className="bg-white border border-slate-200 p-5 rounded-2xl shadow-sm hover:border-blue-200 transition-all group">
                                                        <div className="flex justify-between items-start mb-3">
                                                            <div className="flex items-center gap-2">
                                                                <span className={`w-2 h-2 rounded-full ${
                                                                    risk.impact === 'Critical' ? 'bg-red-500' : 
                                                                    risk.impact === 'High' ? 'bg-orange-500' : 'bg-blue-500'
                                                                }`}></span>
                                                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{risk.category}</span>
                                                            </div>
                                                            <div className="text-right">
                                                                <p className="text-lg font-black text-slate-900 leading-none">{risk.score}</p>
                                                                <p className="text-[8px] font-bold text-slate-400 uppercase">Risk Rank</p>
                                                            </div>
                                                        </div>
                                                        <h5 className="text-sm font-bold text-slate-800 mb-3 group-hover:text-blue-600 transition-colors">{risk.threat}</h5>
                                                        <div className="flex items-center justify-between pt-3 border-t border-slate-50">
                                                            <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded ${
                                                                risk.status === 'Mitigated' ? 'bg-emerald-100 text-emerald-700' : 
                                                                risk.status === 'Active Process' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'
                                                            }`}>
                                                                {risk.status}
                                                            </span>
                                                            <span className="text-[9px] font-bold text-slate-400">{risk.likelihood} Likelihood</span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>
            </section>
        </div>
    );
};

export default GlobalMasterRiskRegister;
